/* Copyright Levelise Ltd 2019 - 2026 */
import React, { Component } from 'react';
import config from '../config';
import FleetService from '../services/fleet-service';

const BY_SECOND_INTERVAL_MS = 6000;
const BY_MINUTE_INTERVAL_MS = 60000;

const FacilityContext = React.createContext({
	facility: {},
	facilityName: '',
	selectedTimezone: '',
	status: {},
	preferences: {},
	selectedTimeFrame: '',
	startDate: null,
	endDate: null,
	records: [],
	liveRecord: null,
	liveRecordByMinute: null,
	forecasts: null,
	forecastsOriginator: 'unknown',
	forecastsRange: 'Hide',
	forecastOriginators: [],
	forecastStartTime: null,
	resetZoom: false,
	timeFrame: '',
	dates: {},
	timeFrameChanging: false,
	error: null,
	setFacility: () => {},
	setFacilityName: () => {},
	setSelectedTimezone: () => {},
	setStatus: () => {},
	setPreferences: () => {},
	setSelectedTimeFrame: () => {},
	setStartDate: () => {},
	setEndDate: () => {},
	setRecords: () => {},
	setLiveRecord: () => {},
	setLiveRecordByMinute: () => {},
	setForecasts: () => {},
	setForecastsRange: () => {},
	setForecastsOriginator: () => {},
	setForecastOriginators: () => {},
	setForecastStartTime: () => {},
	setResetZoom: () => {},
	updateRecords: () => {},
	setTimeFrameChanging: () => {},
	setError: () => {},
	clearError: () => {},
	clearState: () => {},
	resetState: () => {},
});

export default FacilityContext;

export class FacilityProvider extends Component {
	state = {
		facility: {},
		facilityName: '',
		selectedTimezone: '',
		status: {},
		preferences: { [config.energyData]: true, [config.logsPanel]: true, [config.facilityInfo]: true },
		selectedTimeFrame: '',
		startDate: null,
		endDate: null,
		records: [],
		liveRecord: null,
		liveRecordByMinute: null,
		forecasts: null,
		forecastsOriginator: 'unknown',
		forecastsRange: 'Hide',
		forecastOriginators: [],
		forecastStartTime: null,
		resetZoom: false,
		timeFrame: '',
		dates: { start: 'DD/MM/YY', end: 'DD/MM/YY' },
		timeFrameChanging: false,
		error: null,
	};

	bySecondInterval = null;
	byMinuteInterval = null;

	componentDidUpdate(prevProps, prevState) {
		
		const prevDruId = prevState.facility?.druId;
		const currDruId = this.state.facility?.druId;
		const druIdChanged = prevDruId !== currDruId;
		const facilityNameChanged = prevState.facilityName !== this.state.facilityName;
		const prevHasHwt = !!prevState.facility && Object.hasOwn(prevState.facility, 'hotWaterTank');
		const currHasHwt = !!this.state.facility && Object.hasOwn(this.state.facility, 'hotWaterTank');
		const hwtChanged = prevHasHwt !== currHasHwt;

		if (druIdChanged || facilityNameChanged) {
			this.restartBySecondPoll();
		}

		if (druIdChanged || facilityNameChanged || hwtChanged) {
			this.restartByMinutePoll();
		}
	}

	componentWillUnmount() {
		this.stopBySecondPoll();
		this.stopByMinutePoll();
	}

	fetchLiveRecord = () => {
		const { facility, facilityName } = this.state;
		if (!facility?.druId || !facilityName) return;
		const facilityNameAtFetch = facilityName;
		FleetService.getFacilityRecords(facilityName, 'second', null, null, 1)
			.then((records) => {
				// Guard against late-arriving results after the user has navigated to a different
				// facility (e.g. /facility/A → /facility/B via NavBar) so we never write A's
				// record into state that's now displaying B.
				if (this.state.facilityName !== facilityNameAtFetch) return;
				if (records && records.length > 0) {
					this.setState({ liveRecord: records[0] });
				} else {
					this.setState({ liveRecord: null });
				}
			})
			.catch((err) => {
				if (this.state.facilityName !== facilityNameAtFetch) return;
				this.setState({ liveRecord: null });
				this.setError(err);
			});
	};

	fetchLiveRecordByMinute = () => {
		const { facility, facilityName } = this.state;
		if (!facility?.druId || !facilityName) return;
		const facilityNameAtFetch = facilityName;
		FleetService.getFacilityRecords(facilityName, 'minute', null, null, 1)
			.then((records) => {
				if (this.state.facilityName !== facilityNameAtFetch) return;
				if (records && records.length > 0) {
					this.setState({ liveRecordByMinute: records[0] });
				} else {
					this.setState({ liveRecordByMinute: null });
				}
			})
			.catch((err) => {
				if (this.state.facilityName !== facilityNameAtFetch) return;
				this.setError(err);
			});
	};

	restartBySecondPoll = () => {
		this.stopBySecondPoll();
		if (this.state.facility?.druId && this.state.facilityName) {
			this.fetchLiveRecord();
			this.bySecondInterval = setInterval(this.fetchLiveRecord, BY_SECOND_INTERVAL_MS);
		} else {
			this.setState({ liveRecord: null });
		}
	};

	restartByMinutePoll = () => {
		this.stopByMinutePoll();
		if (
			this.state.facility?.druId &&
			this.state.facilityName &&
			Object.hasOwn(this.state.facility, 'hotWaterTank')
		) {
			this.fetchLiveRecordByMinute();
			this.byMinuteInterval = setInterval(this.fetchLiveRecordByMinute, BY_MINUTE_INTERVAL_MS);
		} else {
			this.setState({ liveRecordByMinute: null });
		}
	};

	stopBySecondPoll = () => {
		if (this.bySecondInterval) {
			clearInterval(this.bySecondInterval);
			this.bySecondInterval = null;
		}
	};

	stopByMinutePoll = () => {
		if (this.byMinuteInterval) {
			clearInterval(this.byMinuteInterval);
			this.byMinuteInterval = null;
		}
	};

	setLiveRecord = (liveRecord) => this.setState({ liveRecord });
	setLiveRecordByMinute = (liveRecordByMinute) => this.setState({ liveRecordByMinute });

	setFacility = (facility) => this.setState({ facility });
	setFacilityName = (facilityName) => this.setState({ facilityName });
	setSelectedTimezone = (selectedTimezone) => this.setState({ selectedTimezone });
	setStatus = (status) => this.setState({ status });
	setSelectedTimeFrame = (selectedTimeFrame) => this.setState({ selectedTimeFrame });
	setStartDate = (startDate) => this.setState({ startDate });
	setEndDate = (endDate) => this.setState({ endDate });
	setRecords = (records, timeFrame, dates = { start: 'DD/MM/YY', end: 'DD/MM/YY' }) =>
		this.setState({ records, timeFrame, dates });
	setForecasts = (forecasts) => this.setState({ forecasts });
	setForecastsRange = (forecastsRange) => this.setState({ forecastsRange });
	setForecastsOriginator = (forecastsOriginator) => this.setState({ forecastsOriginator });
	setForecastOriginators = (forecastOriginators) => this.setState({ forecastOriginators });
	setForecastStartTime = (forecastStartTime) => this.setState({ forecastStartTime });
	setPreferences = (preferences) => this.setState({ preferences });
	setResetZoom = () => this.setState({ resetZoom: !this.state.resetZoom });

	updateRecords = (records, timeFrame) => {
		if (timeFrame === this.state.timeFrame) {
			const tempRecords = [...this.state.records];
			for (let i = 0; i < records.length; i++) {
				tempRecords.push(records[i]);
				tempRecords.shift();
			}

			this.setState({ records: tempRecords });
		}
	};

	setTimeFrameChanging = (value) => {
		this.setState({ timeFrameChanging: value });
	};

	clearState = () => {
		this.stopBySecondPoll();
		this.stopByMinutePoll();
		this.setState({
			facility: {},
			facilityName: '',
			selectedTimezone: '',
			status: {},
			preferences: { [config.energyData]: true, [config.logsPanel]: true, [config.facilityInfo]: true },
			selectedTimeFrame: '',
			startDate: null,
			endDate: null,
			records: [],
			liveRecord: null,
			liveRecordByMinute: null,
			forecasts: null,
			forecastsOriginator: 'unknown',
			forecastsRange: 'Hide',
			forecastOriginators: [],
			forecastStartTime: null,
			resetZoom: false,
			timeFrame: '',
			dates: { start: 'DD/MM/YY', end: 'DD/MM/YY' },
			timeFrameChanging: false,
			error: null,
		});
	};

	resetState = () => {
		this.stopBySecondPoll();
		this.stopByMinutePoll();
		this.setState({
			facility: {},
			facilityName: '',
			status: {},
			preferences: { [config.energyData]: true, [config.logsPanel]: true, [config.facilityInfo]: true },
			selectedTimeFrame: '',
			startDate: null,
			endDate: null,
			records: [],
			liveRecord: null,
			liveRecordByMinute: null,
			forecasts: null,
			forecastsOriginator: 'unknown',
			forecastsRange: 'Hide',
			forecastOriginators: [],
			forecastStartTime: null,
			resetZoom: false,
			timeFrame: '',
			dates: { start: 'DD/MM/YY', end: 'DD/MM/YY' },
			timeFrameChanging: false,
			error: null,
		});
	};

	setError = (error) => {
		if (!this.state.error) this.setState({ error });
	};

	clearError = () => this.setState({ error: null });

	render() {
		const contextValue = {
			facility: this.state.facility,
			facilityName: this.state.facilityName,
			selectedTimezone: this.state.selectedTimezone,
			status: this.state.status,
			preferences: this.state.preferences,
			selectedTimeFrame: this.state.selectedTimeFrame,
			startDate: this.state.startDate,
			endDate: this.state.endDate,
			timeFrame: this.state.timeFrame,
			resetZoom: this.state.resetZoom,
			records: this.state.records,
			liveRecord: this.state.liveRecord,
			liveRecordByMinute: this.state.liveRecordByMinute,
			forecasts: this.state.forecasts,
			forecastsRange: this.state.forecastsRange,
			forecastsOriginator: this.state.forecastsOriginator,
			forecastOriginators: this.state.forecastOriginators,
			forecastStartTime: this.state.forecastStartTime,
			dates: this.state.dates,
			timeFrameChanging: this.state.timeFrameChanging,
			error: this.state.error,

			setFacility: this.setFacility,
			setFacilityName: this.setFacilityName,
			setSelectedTimezone: this.setSelectedTimezone,
			setStatus: this.setStatus,
			setPreferences: this.setPreferences,
			setSelectedTimeFrame: this.setSelectedTimeFrame,
			setStartDate: this.setStartDate,
			setEndDate: this.setEndDate,
			setRecords: this.setRecords,
			setLiveRecord: this.setLiveRecord,
			setLiveRecordByMinute: this.setLiveRecordByMinute,
			setResetZoom: this.setResetZoom,
			updateRecords: this.updateRecords,
			setForecasts: this.setForecasts,
			setForecastsRange: this.setForecastsRange,
			setForecastsOriginator: this.setForecastsOriginator,
			setForecastOriginators: this.setForecastOriginators,
			setForecastStartTime: this.setForecastStartTime,
			setTimeFrameChanging: this.setTimeFrameChanging,
			clearState: this.clearState,
			setError: this.setError,
			clearError: this.clearError,
			resetState: this.resetState,
		};
		return <FacilityContext.Provider value={contextValue}>{this.props.children}</FacilityContext.Provider>;
	}
}
